<template>
    <div>
        <h2>{{ step.title }}</h2>
        <p>If you need to leave this page, your answers will be saved for 24 hours. </p>
        <p class="step__notice">
            All fields with an asterisk are required
            <i class="fas fa-asterisk"></i>
        </p>
        <p>
            We try our best to welcome everyone, no matter their circumstances or history. But we must make sure that everyone on our courses is supported appropriately.
        </p>
        <p>
            <b>Please be honest and share as much information as you can - the more we understand, the better we can help you.</b>
        </p>

        <InputCheckboxGroup
            name="involved_in_services"
            :required="checkIsRequired('involved_in_services')"
            :error="formRef.errors.get('involved_in_services')"
            v-model="formRef.involved_in_services"
            :options="getField('involved_in_services')['options']"
            :form="formRef"
            @textarea-updated="updateTextArea"
            @update:modelValue="clearSupportServices"
        >
            <template v-slot:question>
                {{ getField("involved_in_services").question }}
            </template>
            <template v-slot:description>
                {{ getField("involved_in_services").description }}
            </template>
        </InputCheckboxGroup>

        <InputRadioGroup
            name="have_criminal_record"
            :required="checkIsRequired('have_criminal_record')"
            :error="formRef.errors.get('have_criminal_record')"
            v-model="formRef.have_criminal_record"
            :options="getField('have_criminal_record')['options']"
            @input="formRef.errors.clear('have_criminal_record')"
        >
            <template v-slot:question>
                {{ getField("have_criminal_record").question }}
            </template>
            <template v-slot:description>
                <small class="mb-3 d-block">
                    An unspent conviction will still appear on a person’s basic criminal record and should be declared when applying for Prince’s Trust programmes. 
                </small>
                <small class="mb-3 d-block">
                    Please answer honestly, this will mean we can support you in the most appropriate way. 
                </small>
                <h6>How do I know if my conviction is unspent?</h6>
                <small class="d-block mb-3">Under The Rehabilitation of Offenders Act 1974, convictions are considered unspent if a person has received a sentence of more than 4 years or has not completed a ‘rehabilitation period’. The rehabilitation period depends on:</small>
                <ul>
                    <li>The sentence given as a result of a conviction</li>
                    <li>How old the person is at the time of conviction</li>
                </ul>
                <small class="d-block mb-3"><a href="https://www.gov.uk/guidance/rehabilitation-periods">Find out more about rehabilitation periods</a></small>
                <small class="d-block mb-3">
                    You can <a href="https://www.gov.uk/tell-employer-or-college-about-criminal-record/check-your-conviction-caution">use this tool</a> to find out if your conviction is spent or unspent. 
                </small>
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            name="extra_skill_support_needed"
            :required="checkIsRequired('extra_skill_support_needed')"
            :error="formRef.errors.get('extra_skill_support_needed')"
            v-model="formRef.extra_skill_support_needed"
            :options="getField('extra_skill_support_needed')['options']"
            @input="formRef.errors.clear('extra_skill_support_needed')"
        >
            <template v-slot:question>
                {{ getField("extra_skill_support_needed").question }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            v-if="displayFieldForCourse('extra_computer_internet_support')"
            name="extra_computer_internet_support"
            :required="checkIsRequired('extra_computer_internet_support')"
            :error="formRef.errors.get('extra_computer_internet_support')"
            v-model="formRef.extra_computer_internet_support"
            :options="getField('extra_computer_internet_support')['options']"
            @input="formRef.errors.clear('extra_computer_internet_support')"
        >
            <template v-slot:question>
                {{ getField("extra_computer_internet_support").question }}
            </template>
        </InputRadioGroup>

         <InputText
            name="extra_skill_support_needed_more"
            :required="checkIsRequired('extra_skill_support_needed_more')"
            :error="formRef.errors.get('extra_skill_support_needed_more')"
            v-model="formRef.extra_skill_support_needed_more"
            :is-multiline="true"
        >
            <template v-slot:question>
                {{ getField("extra_skill_support_needed_more").question }}
            </template>
        </InputText>
    </div>
</template>


<script setup>
import InputText from "../../../Form/InputText";
import InputRadioGroup from "../../../Form/InputRadioGroup";
import InputCheckboxGroup from "../Form/InputCheckboxGroup";
import Form from "../../../../Form";
import { isRequired } from "./formHelper.js";
import { ref, markRaw } from "vue";

const props = defineProps({
    step: Object,
    fields: Object,
    formData: Object,
    course: String
})

let formRef = markRaw(ref(new Form({
    have_criminal_record: null,
    extra_skill_support_needed: null,
    extra_skill_support_needed_more: null,
    extra_computer_internet_support: null,
    involved_in_services: [],
    all_social_involved_keyworker_text: null,
    all_social_involved_other_text: null,
    alcohol_drug_dependent: null
}, props.formData)))

const getField = (fieldName) => {
   return props.fields[fieldName];
}

const checkIsRequired = (fieldName) => {
   var fieldData = getField(fieldName);
   return isRequired(fieldData, formRef.value);
}

function updateTextArea(field) {
    formRef.value[field.field] = field.value
}

function clearSupportServices(value) {
    if (value.includes('involved_none_above')) {
        formRef.value['involved_in_services'].push['involved_none_above'];
    }
    if (value.includes('involved_none_above') && 
    (value.includes('involved_social_coordinator') || value.includes('involved_police') || value.includes('involved_probation_office') || value.includes('involved_social_worker') || value.includes('involved_support_worker') || value.includes('involved_youth_team') || value.includes('involved_keyworker') || value.includes('involved_other'))) 
    {
        var index = value.indexOf('involved_none_above');
        formRef.value['involved_in_services'].splice(index, 1);
    }
}

function displayFieldForCourse(fieldName) {
    return getField(fieldName).applicable_courses.includes(
        props.course
    );
}

defineExpose({
 formRef
})
</script>